import { Component, signal, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-cell-phone-change',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatStepperModule,
  ],
  standalone: true,
  templateUrl: './user-cell-phone-change.component.html',
  styleUrl: './user-cell-phone-change.component.scss',
})
export class UserCellPhoneChangeComponent implements OnDestroy {
  cellPhoneForm!: FormGroup;
  userForm!: FormGroup;

  step = signal(0);

  token: Token | null = null;
  user: any = null;

  resendCellPhoneCodeTimerInitial = 30;
  resendCellPhoneCodeInterval: any = null;
  resendCellPhoneCodeTimer = signal(0);

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService,
    private router: Router
  ) {
    if (!environment.allowCellPhoneChange) {
      this.router.navigate(['/account']);
      return;
    }

    this.cellPhoneForm = this.fb.group({
      cell_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    });

    this.userForm = this.fb.group({
      cell_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      token_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      token_uuid: ['', Validators.required],
    });

    this.user = this.authService.getUserFromStorage();

    if (this.user) {
      this.cellPhoneForm.patchValue({
        cell_phone: this.user.cell_phone,
      });
    }
  }

  createToken(): void {
    if (this.cellPhoneForm.invalid) {
      this.appService.showSnackBar('Please enter a valid cell phone number', 'error');
      return;
    }

    if (!this.formChanged()) {
      this.appService.showSnackBar('No changes made', 'info');
      return;
    }

    const data = {
      identifier: this.cellPhoneForm.value.cell_phone,
      unique_user_identifier: true,
      delivery_method: 'sms',
    };

    this.token = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.token = response.data;

        this.step.set(1);

        this.startCellPhoneCodeResendTimer();

        this.userForm.patchValue({
          cell_phone: this.cellPhoneForm.value.cell_phone,
          token_uuid: this.token?.uuid,
        });
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  updateCellPhone(): void {
    if (this.userForm.invalid) {
      this.appService.showSnackBar('Please enter a valid cell phone number and token', 'error');
      return;
    }

    this.authService.updateUserCellPhone(this.userForm.value).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
        this.authService.setUserToStorage(response.data);
        this.router.navigate(['/account']);
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  startCellPhoneCodeResendTimer(): void {
    this.resendCellPhoneCodeTimer.set(this.resendCellPhoneCodeTimerInitial);

    this.resendCellPhoneCodeInterval = setInterval(() => {
      if (this.resendCellPhoneCodeTimer() > 0) {
        this.resendCellPhoneCodeTimer.set(this.resendCellPhoneCodeTimer() - 1);
      } else {
        this.clearCellPhoneCodeResendTimer();
      }
    }, 1000);
  }

  clearCellPhoneCodeResendTimer(): void {
    if (this.resendCellPhoneCodeInterval) {
      clearInterval(this.resendCellPhoneCodeInterval);
      this.resendCellPhoneCodeInterval = null;
    }
  }

  changeCellPhone(): void {
    this.cellPhoneForm.reset();

    if (this.user) {
      this.cellPhoneForm.patchValue({
        cell_phone: this.user.cell_phone,
      });
    }

    this.userForm.reset();
    this.token = null;
    this.step.set(0);
    this.clearCellPhoneCodeResendTimer();
  }

  formChanged(): boolean {
    const cellPhone = this.cellPhoneForm.get('cell_phone')?.value;

    if (this.user) {
      return cellPhone !== this.user.cell_phone;
    }

    return false;
  }

  goBack(): void {
    this.router.navigate(['/account']);
  }

  ngOnDestroy(): void {
    this.clearCellPhoneCodeResendTimer();
  }
}